import React, { useMemo } from 'react';
import { flex } from '@fabric-ui/theme';
import useQueryObject from '@hooks/useQueryObjectSql';
import { Box, Skeleton, Typography } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { DwQueryRequest } from '../../CustomDashboards/types';
import { calculatePercentageChange, getNumericValue } from '../utils';
import { getAgentsHumanEstimation } from './queries';
import StatusChange from './statusChange';
import { AgentTimeSavedCardProps } from './types';
import { getLast7DaysDate } from './utils';

const QUERY_OPTIONS = { placeholderData: keepPreviousData };

const extractFirstItem = (data: any) => data?.[0];

const AgentTimeSavedCard: React.FC<AgentTimeSavedCardProps> = ({ filterContext, title, unit = '', percentageSub, percentageFormula }) => {
  const queryObject = useMemo(() => [getAgentsHumanEstimation(filterContext) as DwQueryRequest], [filterContext]);

  const last7DaysQueryObject = useMemo(
    () => [
      getAgentsHumanEstimation({
        ...filterContext,
        dateRange: [getLast7DaysDate(filterContext.dateRange[1]), filterContext.dateRange[1]],
      }) as DwQueryRequest,
    ],
    [filterContext]
  );

  const agentHumanEstimation = useQueryObject({
    queryObject,
    options: QUERY_OPTIONS,
    onSuccess: extractFirstItem,
  });

  const agentHumanEstimationInLast7Days = useQueryObject({
    queryObject: last7DaysQueryObject,
    options: QUERY_OPTIONS,
    onSuccess: extractFirstItem,
  });

  const currentEstimate = agentHumanEstimation?.data?.human_estimate;
  const previousEstimate = agentHumanEstimationInLast7Days?.data?.human_estimate;

  const percentage = useMemo(
    () => calculatePercentageChange(getNumericValue(previousEstimate), getNumericValue(currentEstimate)),
    [previousEstimate, currentEstimate]
  );

  const isLoading =
    agentHumanEstimation.isLoading ||
    agentHumanEstimation.isRefetching ||
    agentHumanEstimationInLast7Days.isLoading ||
    agentHumanEstimationInLast7Days.isRefetching;

  const isFTEMultiplier = title === 'FTE Multiplier';

  return (
    <Box sx={{ padding: '16px 24px', background: 'var(--color-surface-base-primary)', minHeight: '137px', height: '100%', ...flex.col }}>
      <Typography sx={{ fontSize: '14px', marginBottom: '16px', color: 'var(--color-content-base-tertiary)' }} variant="h2">
        {title}
      </Typography>
      {!isLoading ? (
        <div>
          <Box display="flex" flexDirection="row" alignItems="baseline" gap="8px">
            <Typography variant="h3" sx={{ m: 0, fontSize: '24px', color: 'var(--color-content-base-primary)' }}>
              {currentEstimate != null ? percentageFormula(currentEstimate).toFixed(1) : '0'}
              {isFTEMultiplier ? 'x' : ''}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '13px', color: 'var(--color-content-base-primary)' }}>
              {unit}
            </Typography>
          </Box>
          <Box sx={{ ...flex.itemsCenter, gap: 1, lineHeight: '18px', marginTop: '8px' }}>
            <StatusChange
              value={isFTEMultiplier ? percentage : percentageFormula(getNumericValue(currentEstimate) - getNumericValue(previousEstimate))}
              isROITile
              showPercentage={isFTEMultiplier}
            />
            <Typography variant="body2" sx={{ fontSize: '13px', color: 'var(--color-content-base-subdued)' }}>
              {percentageSub}
            </Typography>
          </Box>
        </div>
      ) : (
        <Skeleton variant="rectangular" width="100%" height="100%" />
      )}
    </Box>
  );
};

export default AgentTimeSavedCard;
